import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { inr, fmtDate, fmtTime } from "@/lib/format";
import Modal, { inputCls, btnGhost, btnPrimary } from "@/components/Modal";
import { ThermalInvoice, A4Invoice } from "@/components/InvoiceView";
import { Search, Eye, Printer, Filter } from "lucide-react";

const ORDER_LABEL = { DINE_IN: "Dine In", TAKEAWAY: "Takeaway", DELIVERY: "Delivery" };

export default function BillHistory() {
  const { user } = useAuth();
  const { settings } = useData();
  const [bills, setBills] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [filters, setFilters] = useState({ invoice_no: "", phone: "", payment_method: "", cashier_id: "", date_from: "", date_to: "" });
  const [view, setView] = useState(null);
  const [printBill, setPrintBill] = useState(null);
  const [printFormat, setPrintFormat] = useState("thermal");

  const load = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/bills", { params });
    setBills(data);
  };

  useEffect(() => {
    load();
    if (user?.role === "admin") api.get("/cashiers").then((r) => setCashiers(r.data.filter((c) => c.role === "cashier")));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const doPrint = (bill, format) => {
    setPrintFormat(format);
    setPrintBill(bill);
    setTimeout(() => {
      window.print();
    }, 150);
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-6">
      <div className="mb-5">
        <h1 className="font-head text-2xl font-bold">Bill History</h1>
        <p className="text-sm text-[#6B7280]">{bills.length} bills</p>
      </div>

      <div className="bg-[#151D2A] border border-[#23334D] rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 text-xs text-[#6B7280] mb-3 uppercase tracking-wide font-semibold"><Filter className="w-3.5 h-3.5" /> Filters</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="relative"><Search className="w-4 h-4 text-[#6B7280] absolute left-2.5 top-1/2 -translate-y-1/2" /><input className={inputCls + " pl-8"} placeholder="Invoice no." value={filters.invoice_no} data-testid="filter-invoice" onChange={(e) => set("invoice_no", e.target.value)} /></div>
          <input className={inputCls} placeholder="Phone" value={filters.phone} onChange={(e) => set("phone", e.target.value)} />
          <select className={inputCls} value={filters.payment_method} onChange={(e) => set("payment_method", e.target.value)}>
            <option value="">All payments</option><option>CASH</option><option>UPI</option><option>CARD</option><option>SPLIT</option>
          </select>
          {user?.role === "admin" && (
            <select className={inputCls} value={filters.cashier_id} onChange={(e) => set("cashier_id", e.target.value)}>
              <option value="">All cashiers</option>
              {cashiers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <input type="date" className={inputCls} value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} />
          <input type="date" className={inputCls} value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} />
        </div>
      </div>

      <div className="bg-[#151D2A] border border-[#23334D] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-xs text-[#6B7280] uppercase tracking-wide border-b border-[#23334D]">
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Cashier</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#23334D]">
              {bills.map((b) => (
                <tr key={b.id} data-testid={`bill-row-${b.id}`} className="hover:bg-[#1E2C45]/40">
                  <td className="px-4 py-3 font-mono font-semibold text-[#10B981]">{b.invoice_no}</td>
                  <td className="px-4 py-3 text-[#9CA3AF]">{fmtDate(b.created_at)}<div className="text-xs text-[#6B7280]">{fmtTime(b.created_at)}</div></td>
                  <td className="px-4 py-3">{b.cashier_name}</td>
                  <td className="px-4 py-3 text-[#9CA3AF]">{ORDER_LABEL[b.order_type]}{b.table_name ? ` · ${b.table_name}` : ""}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded bg-[#1E2C45]">{b.payment?.method || "—"}</span></td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{inr(b.grand_total)}</td>
                  <td className="px-4 py-3 text-center"><span className={`text-xs px-2 py-0.5 rounded ${b.status === "paid" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>{b.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setView(b)} data-testid={`view-bill-${b.id}`} className="p-2 text-[#9CA3AF] hover:text-white"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => doPrint(b, "thermal")} data-testid={`print-bill-${b.id}`} className="p-2 text-[#9CA3AF] hover:text-[#10B981]"><Printer className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {bills.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6B7280]">No bills found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={!!view}
        onClose={() => setView(null)}
        title={view?.invoice_no}
        width="max-w-md"
        footer={
          <>
            <button onClick={() => doPrint(view, "thermal")} className={btnGhost}>Print 80mm</button>
            <button onClick={() => doPrint(view, "a4")} className={btnPrimary}>Print A4</button>
          </>
        }
      >
        {view && (
          <div className="space-y-3 text-sm">
            <Line l="Date" r={`${fmtDate(view.created_at)} ${fmtTime(view.created_at)}`} />
            <Line l="Cashier" r={view.cashier_name} />
            <Line l="Order" r={`${ORDER_LABEL[view.order_type]}${view.table_name ? " · " + view.table_name : ""}`} />
            {view.customer_name && <Line l="Customer" r={view.customer_name} />}
            {view.customer_phone && <Line l="Phone" r={view.customer_phone} />}
            <div className="border-t border-[#23334D] pt-3 space-y-2">
              {view.items.map((it, i) => (
                <div key={i} className="flex justify-between">
                  <span>{it.name}{it.variation ? ` (${it.variation})` : ""} × {it.qty}</span>
                  <span className="font-mono">{inr(it.amount)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-[#23334D] pt-3 space-y-1.5">
              <Line l="Subtotal" r={inr(view.subtotal)} />
              {view.discount_amount > 0 && <Line l="Discount" r={"-" + inr(view.discount_amount)} />}
              {(view.tax_type || settings?.tax_type) === "igst" ? <Line l="IGST" r={inr(view.igst)} /> : <><Line l="CGST" r={inr(view.cgst)} /><Line l="SGST" r={inr(view.sgst)} /></>}
              <div className="flex justify-between font-bold text-lg pt-1"><span>Grand Total</span><span className="font-mono text-[#10B981]">{inr(view.grand_total)}</span></div>
            </div>
          </div>
        )}
      </Modal>

      {printBill && printFormat === "thermal" && <ThermalInvoice bill={printBill} settings={settings} />}
      {printBill && printFormat === "a4" && <A4Invoice bill={printBill} settings={settings} />}
    </div>
  );
}

const Line = ({ l, r }) => (
  <div className="flex justify-between"><span className="text-[#9CA3AF]">{l}</span><span>{r}</span></div>
);
